// TaskTypeDatePicker.tsx
import React, { FC, useMemo } from 'react'
import { DatePicker, DatePickerProps } from '@mui/x-date-pickers'
import dayjs, { Dayjs } from 'dayjs'
import { Box, SxProps, useTheme } from '@mui/material'

type DPProps = DatePickerProps<Dayjs>
type DPSlotProps = DPProps['slotProps']

interface TaskTypeDatePickerProps {
  label: string
  value: Dayjs | null
  onChange: (d: Dayjs | null) => void
  disabled: boolean
  /** Pass directly to MUI DatePicker slotProps (e.g. { textField: { size: 'small', sx: {...} } }) */
  slotProps?: DPSlotProps
}

const TaskTypeDatePicker: FC<TaskTypeDatePickerProps> = ({ label, value, onChange, disabled, slotProps }) => {
  const theme = useTheme()

  const textFieldStyles: SxProps = {
    '& .Mui-disabled': { color: theme.palette.text.primary },
    '& .MuiInputBase-input': {
      color: theme.palette.text.primary,
      WebkitTextFillColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,1)' : 'rgba(0,0,0,1)',
      '--color': theme.palette.mode === 'dark' ? 'rgba(255,255,255,1)' : 'rgba(0,0,0,1)',
    },
    '& .MuiInputBase-root.Mui-disabled': {
      color: theme.palette.text.primary,
      bgcolor: 'background.transparent',
    },
  }

  // Merge caller slotProps with our text styles
  const mergedSlotProps: DPSlotProps = useMemo(() => {
    const incomingTF = (slotProps?.textField ?? {}) as any
    const mergedSx = incomingTF?.sx ? [incomingTF.sx, textFieldStyles] : textFieldStyles

    return {
      ...slotProps,
      textField: {
        ...incomingTF,
        disabled, // allow parent to control disabled
        sx: mergedSx, // merge sx safely
      },
    }
  }, [slotProps, disabled, textFieldStyles])
  return (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      <DatePicker
        label={label || ''}
        value={value ? dayjs(value) : null}
        onChange={onChange}
        disabled={disabled}
        slotProps={mergedSlotProps}
      />
    </Box>
  )
}

export default TaskTypeDatePicker
